/**
 * MentionHighlightTextarea — a plain textarea whose tagged `@mentions` show in the tag
 * color while the reader is still typing.
 *
 * A textarea cannot style a word, so a mirror div sits behind it with the SAME classes
 * (same padding, font and wrapping) and paints the text, tags colored; the textarea's own
 * glyphs turn transparent and only its caret shows. The mirror only exists while a tag
 * is in the text, so an untagged message is the untouched textarea.
 *
 * Tag colors are the questionnaire builder's concept chip (`ConceptMentionField`).
 * The composer colors text only: a heavier weight or horizontal padding would change
 * glyph widths and pull the mirror out of line with the caret.
 */

import React, { useRef } from "react";

import { cn } from "@/lib/utils";

/** Tag text color, shared by the composer, the sent bubble and the topic labels. */
export const MENTION_TAG_TEXT_CLASS = "text-[#7C3AED]";

/** Tag chip fill. A box-shadow pads it visually without moving the text. */
const MENTION_TAG_FILL_CLASS = "rounded-[3px] bg-[#EEE4FF] shadow-[0_0_0_2px_#EEE4FF]";

export interface MentionTagSegment {
  text: string;
  isTag: boolean;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Splits `text` into plain runs and `@token` tags (whole words only, case-sensitive). */
export function splitMentionTags(text: string, tokens: Array<string>): Array<MentionTagSegment> {
  const uniqueTokens = [...new Set(tokens.filter((token) => token.length > 0))];
  if (uniqueTokens.length === 0 || text.length === 0) {
    return [{ text, isTag: false }];
  }
  // Longest first, so `@AnnaSmith` wins over `@Anna`.
  uniqueTokens.sort((a, b) => b.length - a.length);
  const pattern = new RegExp(
    `(^|[^\\w])(@(?:${uniqueTokens.map(escapeRegExp).join("|")}))(?!\\w)`,
    "g",
  );
  const segments: Array<MentionTagSegment> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    const tagStart = match.index + match[1].length;
    if (tagStart > lastIndex) {
      segments.push({ text: text.slice(lastIndex, tagStart), isTag: false });
    }
    segments.push({ text: match[2], isTag: true });
    lastIndex = tagStart + match[2].length;
  }
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), isTag: false });
  }
  return segments;
}

interface MentionHighlightTextareaProps {
  textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement> &
    React.RefAttributes<HTMLTextAreaElement> & { "data-testid": string };
  /** The textarea's classes; the mirror reuses them so both lay text out identically. */
  className: string;
  /** Classes for the positioning wrapper (grid area, flex sizing). */
  wrapperClassName?: string;
  /** Caret color while the textarea's glyphs are transparent. */
  caretClassName: string;
  /** Tokens (without `@`) to show as tags. */
  tags: Array<string>;
}

export function MentionHighlightTextarea(props: MentionHighlightTextareaProps) {
  const { textareaProps, className, wrapperClassName, caretClassName, tags } = props;

  const mirrorRef = useRef<HTMLDivElement>(null);

  const value = typeof textareaProps.value === "string" ? textareaProps.value : "";
  const segments = splitMentionTags(value, tags);
  const hasTags = segments.some((segment) => segment.isTag);

  const onScroll = (event: React.UIEvent<HTMLTextAreaElement>) => {
    if (mirrorRef.current) {
      mirrorRef.current.scrollTop = event.currentTarget.scrollTop;
    }
    textareaProps.onScroll?.(event);
  };

  const mirror = hasTags ? (
    <div
      ref={mirrorRef}
      aria-hidden="true"
      data-testid="chat-composer-mention-mirror"
      className={cn(
        className,
        "pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words",
      )}
    >
      {segments.map((segment, index) =>
        segment.isTag ? (
          <span
            key={index}
            className={cn(MENTION_TAG_TEXT_CLASS, MENTION_TAG_FILL_CLASS)}
            data-testid="chat-composer-mention-tag"
          >
            {segment.text}
          </span>
        ) : (
          <React.Fragment key={index}>{segment.text}</React.Fragment>
        ),
      )}
      {/* A trailing newline needs a glyph after it to take its line, as the textarea does. */}
      {value.endsWith("\n") ? " " : null}
    </div>
  ) : null;

  return (
    <div className={cn("relative", wrapperClassName)}>
      {mirror}
      <textarea
        {...textareaProps}
        onScroll={onScroll}
        className={cn(className, "relative", hasTags && cn("text-transparent", caretClassName))}
      />
    </div>
  );
}
